<template>
  <li
    role="listitem"
    :class="{ 'open-submenu': title && isOpenSubmenu, 'inverted': isDarkTheme }"
    @mouseover="openSubmenu"
    @mouseleave="closeSubmenu"
    @keyup.enter="openSubmenu"
    @keyup.esc="closeSubmenu"
    @click="removeFocus"
  >
    <span
      v-if="title"
      class="link with-arrow"
      :class="[{ expanded: isOpenSubmenu }]"
      :aria-expanded="isOpenSubmenu"
      :aria-haspopup="true"
      tabindex="0"
      @click="emitClick"
    >
      {{ titleValue }}
      <sh-icon
        class="arrow"
        size="xsmall"
        name="arrowRight"
      />
    </span>
    <sh-button
      v-else
      class="link"
      :naked="true"
      v-bind="handleLinkAttrs"
      role="link"
    >
      {{ linkText }}
    </sh-button>
    <slot name="subMenu"/>
  </li>
</template>


<script>
import { createNamespacedHelpers } from 'vuex'

import { ShIcon } from '@sohohouse/design-system'
import get from '~/helpers/get'

const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  components: {
    ShIcon
  },
  data () {
    return {
      isOpenSubmenu: false
    }
  },
  props: {
    link: {
      type: Object
    },
    title: {
      type: Object
    }
  },
  computed: {
    ...mapEnvGetters(['isDarkTheme']),
    titleValue () {
      return get(this.title, 'value')
    },
    linkHref () {
      return get(this.link, 'value.href')
    },

    linkTarget () {
      return get(this.link, 'value.target')
    },

    linkType () {
      return get(this.link, 'value.linktype')
    },
    linkText () {
      return get(this.link, 'value.text')
    },
    isInternalLink () {
      return this.linkType === 'internal'
    },
    handleLinkAttrs () {
      if (this.linkHref) {
        return this.isInternalLink
          ?
          {
            tag: 'nuxt-link',
            to: this.linkHref,
            target: this.linkTarget
          }
          :
          {
            tag: 'a',
            href: this.linkHref,
            target: this.linkTarget
          }
      }

      return { tag: 'button' }
    },
    isMobile () {
      return this.$device.isMobile
    },
    onDesktopWithSubmenu () {
      return this.title && !this.isMobile
    }
  },
  methods: {
    emitClick () {
      this.$emit('clicked:title')
    },
    openSubmenu () {
      if (this.onDesktopWithSubmenu) {
        this.isOpenSubmenu = true
        document.body.classList.add('overlay')
      }
    },
    closeSubmenu () {
      if (this.onDesktopWithSubmenu) {
        this.isOpenSubmenu = false
        document.body.classList.remove('overlay')
      }
    },
    removeFocus (event) {
      if (this.onDesktopWithSubmenu) {
        event.target.blur()
      }
    }
  }
}
</script>

<style lang="scss" scoped>

$line-height-small: 18px;
$line-height-medium: 22px;

li {
  list-style: none;
}

li > .link {
  color: $color-black;
  @include font('060');

  &:hover {
    @include underline;

    &:after {
      bottom: -#{$space-010};
    }
  }

  &:focus {
    box-shadow: 0 0 4px 0 $color-black;
    border-radius: 4px;
    outline: none;

    &:after {
      content: none;
    }
  }
}

li.inverted > .link {
  color: $color-white;

  &:hover {
    @include underline;

    &:after {
      background-color: $color-white;
      bottom: -#{$space-010};
    }
  }

  &:focus {
    box-shadow: 0 0 4px 0 $color-white;
  }
}

li > .link.with-arrow {
  position: relative;
  cursor: pointer;
  display: block;
  line-height: $line-height-small;
  z-index: 6;

  @include tablet {
    line-height: $line-height-medium;
  }

  .arrow {
    position: absolute;
    bottom: 4px;
    right: $space-040;

    @include tablet {
      display: none;
    }
  }

  &:hover {
    &:after {
      content: none;
    }

    @include tablet {
      @include underline;

      &:after {
        bottom: -#{$space-010};
      }
    }
  }
}

li > .link.expanded {
  @include tablet {
    @include underline;

    &:after {
      bottom: -#{$space-010};
    }
  }
}

li.inverted > .link.expanded {
  @include tablet {
    @include underline;

    &:after {
      background-color: $color-white;
      bottom: -#{$space-010};
    }
  }
}
</style>
